import { useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  REGEN_SCOPES,
  useGrantCredits,
  useRefundPayment,
  useRegenerateJob,
  useSetAdmin,
  useStartRegenerate,
  useUserCredits,
  useUserPayments,
  type PaymentRow,
} from "@/api/admin/users";
import { ApiError } from "@/api/client";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { useConfirmedChange } from "@/components/admin/confirmChange";
import { panelTarget } from "./panelTarget";
import { PLATFORM_KEY, displayName, identLabel } from "./userIdentity";

/**
 * DUE-100 Phase F — User detail page. Re-keyed onto `users.id` in DUE-191:
 * iOS guest / Apple-only accounts have no `tg_id`, so the page (and its
 * route, `/admin/users/:userId`) key off the numeric `users.id` instead.
 *
 * Reads `/api/admin/users/{user_id}/credits` and offers admin actions:
 *   - Grant credits  → POST /api/admin/credits/grant (`target_user_id`)
 *   - Refund payment → POST /api/admin/refund
 *   - Admin access   → PATCH /api/admin/users/{user_id}/admin (DUE-180)
 *
 * Block / Reset user is a separate ticket (DUE-230) — no stub lives here
 * until the backend endpoints exist.
 *
 * Status copy uses i18n keys (no glyphs) — mirrors how Phase E surfaced
 * `free_tier.updated` / `free_tier.no_changes`.
 *
 * AdminBlock note (DUE-180): `UserCreditsView` (the payload this page
 * already fetches) carries the target's live `is_admin` flag, so the
 * toggle initializes from server-confirmed state rather than assuming
 * `false`. After a successful PATCH the local state is updated from the
 * response directly (the `["admin","users"]` invalidation from
 * `useSetAdmin` also refetches this credits query in the background).
 */

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const CARD: CSSProperties = {
  background: "var(--d-ink-2)",
  borderRadius: 22,
  padding: 16,
  border: "1px solid var(--d-line)",
};
const ROW: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  padding: "4px 0",
  fontFamily: "var(--font-body)",
  fontSize: 14,
};
const NUM: CSSProperties = {
  fontFamily: "var(--font-mono)",
  color: "var(--d-fg)",
};
const BTN: CSSProperties = {
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid var(--d-violet)",
  background: "transparent",
  color: "var(--d-violet)",
  cursor: "pointer",
  fontFamily: "var(--font-body)",
};
const BTN_DISABLED: CSSProperties = {
  ...BTN,
  opacity: 0.4,
  cursor: "not-allowed",
};
const INPUT: CSSProperties = {
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-mono)",
};
const STATUS: CSSProperties = { fontSize: 12, color: "var(--d-fg-muted)" };
const TABLE: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  marginTop: 8,
};
const TH: CSSProperties = {
  textAlign: "left",
  padding: "4px 6px",
  borderBottom: "1px solid var(--d-line)",
  color: "var(--d-fg-muted)",
  fontFamily: "var(--font-body)",
};
const TD: CSSProperties = {
  padding: "4px 6px",
  borderBottom: "1px solid var(--d-line)",
};

const FEATURES = [
  "synastry",
  "composite",
  "transits_days",
  "legacy_credit",
  "relation_named",
  "relation_words",
] as const;

export function UserDetailPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const { userId: userIdStr } = useParams();
  const userId = Number(userIdStr);
  const { data, isLoading, isError, refetch } = useUserCredits(
    Number.isFinite(userId) ? userId : null,
  );
  const grant = useGrantCredits();
  const refund = useRefundPayment();
  const setAdmin = useSetAdmin();
  const [status, setStatus] = useState<string | null>(null);

  if (isError) return <AdminErrorState onRetry={() => void refetch()} />;

  if (isLoading || !data) {
    return <div style={PAGE}>{t("common.loading")}</div>;
  }

  // Fallback for the refund-confirm copy: prefer the same label the heading
  // shows, else the identifier column (always non-null).
  const userLabel = displayName(data) ?? identLabel(data);

  return (
    <div style={PAGE}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>
        {displayName(data) ?? t("users.no_name")}
      </h2>
      <div style={{ ...STATUS, fontFamily: "var(--font-body)" }}>
        <span>{t(PLATFORM_KEY[data.platform])}</span> ·{" "}
        <span>{identLabel(data)}</span>
      </div>

      <div style={CARD}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
          {t("users.detail.balance")}
        </div>
        <div style={ROW}>
          <span>{t("users.detail.synastry")}</span>
          <span style={NUM}>{data.synastry_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.composite")}</span>
          <span style={NUM}>{data.composite_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.relation_named")}</span>
          <span style={NUM}>{data.relation_named_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.relation_words")}</span>
          <span style={NUM}>{data.relation_words_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.transits_until")}</span>
          <span style={NUM}>{data.transits_until ?? "—"}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.legacy")}</span>
          <span style={NUM}>{data.legacy_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.total_llm")}</span>
          <span style={NUM}>${data.total_llm_cost_usd.toFixed(2)}</span>
        </div>
      </div>

      <GrantBlock
        userId={data.id}
        onGrant={(args) =>
          grant.mutate(args, {
            onSuccess: () => setStatus(t("users.detail.status.granted")),
            onError: (e) => setStatus((e as Error).message),
          })
        }
      />

      <PaymentsBlock
        userId={data.id}
        userLabel={userLabel}
        refund={refund}
        setStatus={setStatus}
      />

      <AdminBlock
        userId={data.id}
        isAdmin={data.is_admin ?? false}
        setAdmin={setAdmin}
        setStatus={setStatus}
      />

      <RegenerateBlock userId={data.id} setStatus={setStatus} />

      {status && (
        <div role="status" style={STATUS}>
          {status}
        </div>
      )}

      <div style={CARD}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
          {t("users.detail.history")}
        </div>
        <table style={TABLE}>
          <thead>
            <tr>
              <th style={TH}>when</th>
              <th style={TH}>feature</th>
              <th style={{ ...TH, textAlign: "right" }}>amount</th>
              <th style={TH}>reason</th>
            </tr>
          </thead>
          <tbody>
            {data.history.map((row) => (
              <tr key={row.id}>
                <td style={TD}>
                  {new Date(row.created_at)
                    .toISOString()
                    .slice(0, 19)
                    .replace("T", " ")}
                </td>
                <td style={TD}>{row.feature}</td>
                <td style={{ ...TD, textAlign: "right" }}>{row.amount}</td>
                <td style={TD}>{row.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GrantBlock(props: {
  userId: number;
  onGrant: (args: {
    target_user_id: number;
    amount: number;
    feature: string;
  }) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const [amount, setAmount] = useState("1");
  const [feature, setFeature] = useState<string>(FEATURES[0]);
  const numericAmount = Number(amount);
  const disabled = !Number.isFinite(numericAmount) || numericAmount <= 0;
  const { confirmChange, modal } = useConfirmedChange();
  return (
    <div style={CARD}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
        {t("users.detail.action.grant")}
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginTop: 8,
          flexWrap: "wrap",
        }}
      >
        <select
          aria-label="feature"
          style={INPUT}
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
        >
          {FEATURES.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <input
          aria-label="amount"
          style={INPUT}
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <button
          style={disabled ? BTN_DISABLED : BTN}
          disabled={disabled}
          onClick={() =>
            confirmChange(
              panelTarget("grant_credits"),
              `${feature}: +0`,
              `${feature}: +${numericAmount}`,
              () =>
                props.onGrant({
                  target_user_id: props.userId,
                  amount: numericAmount,
                  feature,
                }),
            )
          }
        >
          {t("users.detail.action.grant")}
        </button>
      </div>
      {modal}
    </div>
  );
}

/** DUE-492: USD (+ receipt currency for App Store), else receipt price, else Stars. */
export function formatPaymentAmount(p: PaymentRow): string {
  const local =
    p.amount_local != null && p.currency
      ? `${Number(p.amount_local)} ${p.currency}`
      : null;
  if (p.amount_usd != null) {
    const usd = `$${Number(p.amount_usd).toFixed(2)}`;
    return local && p.currency !== "USD" ? `${usd} · ${local}` : usd;
  }
  if (local) return local;
  return p.amount_xtr != null ? String(p.amount_xtr) : "—";
}

function PaymentsBlock(props: {
  userId: number;
  userLabel: string;
  refund: ReturnType<typeof useRefundPayment>;
  setStatus: (s: string) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const { data, isLoading } = useUserPayments(props.userId);
  const { confirmChange, modal } = useConfirmedChange();

  const doRefund = (target: PaymentRow) => {
    props.refund.mutate(
      { payment_id: target.id },
      {
        onSuccess: () => props.setStatus(t("users.detail.status.refunded")),
        onError: (e) =>
          props.setStatus(
            (e as Error).message || t("users.detail.status.refund_error"),
          ),
      },
    );
  };

  return (
    <div style={CARD}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
        {t("users.detail.payments")}
      </div>
      {isLoading ? (
        <div style={STATUS}>{t("common.loading")}</div>
      ) : !data || data.length === 0 ? (
        <div style={STATUS}>{t("users.detail.no_payments")}</div>
      ) : (
        <table style={TABLE}>
          <thead>
            <tr>
              <th style={TH}>{t("users.detail.col_date")}</th>
              <th style={TH}>{t("users.detail.col_sku")}</th>
              <th style={{ ...TH, textAlign: "right" }}>
                {t("users.detail.col_amount")}
              </th>
              <th style={TH}>{t("users.detail.col_status")}</th>
              <th style={{ ...TH, textAlign: "right" }}>
                {t("users.detail.col_action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((p) => (
              <tr key={p.id}>
                <td style={TD}>{new Date(p.created_at).toISOString().slice(0, 10)}</td>
                <td style={TD}>{p.product_code}</td>
                <td style={{ ...TD, textAlign: "right" }}>{formatPaymentAmount(p)}</td>
                <td style={TD}>
                  {t(`users.detail.pstatus.${p.status}`, {
                    defaultValue: p.status,
                  })}
                </td>
                <td style={{ ...TD, textAlign: "right" }}>
                  {p.refundable ? (
                    <button
                      style={BTN}
                      disabled={props.refund.isPending}
                      onClick={() =>
                        confirmChange(
                          panelTarget("refund_stars"),
                          `${p.product_code} · ${p.amount_xtr ?? "?"} XTR paid`,
                          `refunded to ${props.userLabel}`,
                          () => doRefund(p),
                          t("users.detail.confirm.body", {
                            amount: p.amount_xtr ?? "?",
                            user: props.userLabel,
                            sku: p.product_code,
                          }),
                        )
                      }
                    >
                      {t("users.detail.action.refund_stars")}
                    </button>
                  ) : (
                    <span style={STATUS}>
                      {t(`users.detail.pstatus.${p.status}`, {
                        defaultValue: p.status,
                      })}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal}
    </div>
  );
}

/** True for the 400 the backend raises when an admin tries to change their
 * own `is_admin` flag (`{"detail": {"code": "self_admin_change", ...}}` —
 * see `set_admin` in `src/api/routers/admin.py`). */
function isSelfAdminChangeError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 400) return false;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("detail" in body)) {
    return false;
  }
  const detail = (body as { detail?: unknown }).detail;
  return (
    typeof detail === "object" &&
    detail !== null &&
    (detail as { code?: unknown }).code === "self_admin_change"
  );
}

function AdminBlock(props: {
  userId: number;
  isAdmin: boolean;
  setAdmin: ReturnType<typeof useSetAdmin>;
  setStatus: (s: string) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  // Initialized from the server-confirmed flag (DUE-180 Finding 1); kept in
  // local state afterwards so a successful PATCH updates the label
  // immediately without waiting for the credits query to refetch.
  const [isAdmin, setIsAdmin] = useState(props.isAdmin);
  const { confirmChange, modal } = useConfirmedChange();

  const onConfirm = () => {
    const nextIsAdmin = !isAdmin;
    props.setAdmin.mutate(
      { userId: props.userId, is_admin: nextIsAdmin },
      {
        onSuccess: (data) => {
          setIsAdmin(data.is_admin);
          props.setStatus(t("users.detail.admin.status.updated"));
        },
        onError: (e) =>
          props.setStatus(
            isSelfAdminChangeError(e)
              ? t("users.detail.admin.self_error")
              : (e as Error).message,
          ),
      },
    );
  };

  return (
    <div style={CARD}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
        {t("users.detail.admin.title")}
      </div>
      <div style={{ marginTop: 8 }}>
        <button
          style={BTN}
          disabled={props.setAdmin.isPending}
          onClick={() =>
            confirmChange(
              panelTarget("admin_rights"),
              isAdmin ? "Admin" : "Not admin",
              isAdmin ? "Not admin" : "Admin",
              onConfirm,
            )
          }
        >
          {isAdmin
            ? t("users.detail.admin.revoke")
            : t("users.detail.admin.grant")}
        </button>
      </div>
      {modal}
    </div>
  );
}

/** True for the 409 the backend raises while a regeneration job is already
 * running for this user (`{"detail": {"code": "regenerate_running", ...}}`
 * — see `post_regenerate` in `src/api/routers/admin.py`, DUE-296). */
function isRegenerateRunningError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("detail" in body)) {
    return false;
  }
  const detail = (body as { detail?: unknown }).detail;
  return (
    typeof detail === "object" &&
    detail !== null &&
    (detail as { code?: unknown }).code === "regenerate_running"
  );
}

/** DUE-296 — delete-then-regenerate the user's existing readings with the
 * current prompts/config. Four scope checkboxes → ConfirmModal → POST;
 * the job snapshot polls every 2 s while running (`useRegenerateJob`). */
function RegenerateBlock(props: {
  userId: number;
  setStatus: (s: string) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const { data: job } = useRegenerateJob(props.userId);
  const start = useStartRegenerate();
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(REGEN_SCOPES.map((s) => [s, true])),
  );
  const { confirmChange, modal } = useConfirmedChange();

  const scopes = REGEN_SCOPES.filter((s) => checked[s]);
  const disabled =
    scopes.length === 0 || start.isPending || job?.status === "running";

  const onConfirm = () => {
    start.mutate(
      { userId: props.userId, scopes },
      {
        onError: (e) =>
          props.setStatus(
            isRegenerateRunningError(e)
              ? t("users.detail.regenerate.already_running")
              : (e as Error).message,
          ),
      },
    );
  };

  const done = job?.items.filter((i) => i.state === "done").length ?? 0;
  const failed = job?.items.filter((i) => i.state === "failed").length ?? 0;

  return (
    <div style={CARD}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
        {t("users.detail.regenerate.title")}
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginTop: 8,
          flexWrap: "wrap",
          fontFamily: "var(--font-body)",
          fontSize: 14,
        }}
      >
        {REGEN_SCOPES.map((s) => (
          <label
            key={s}
            style={{ display: "flex", gap: 6, alignItems: "center" }}
          >
            <input
              type="checkbox"
              checked={checked[s] ?? false}
              onChange={(e) =>
                setChecked((c) => ({ ...c, [s]: e.target.checked }))
              }
            />
            {t(`users.detail.regenerate.scope.${s}`)}
          </label>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>
        <button
          style={disabled ? BTN_DISABLED : BTN}
          disabled={disabled}
          onClick={() =>
            confirmChange(
              panelTarget("regenerate_readings"),
              "Cached readings",
              `Deleted and regenerated: ${scopes.join(", ")}`,
              onConfirm,
              t("users.detail.regenerate.confirm_body"),
            )
          }
        >
          {t("users.detail.regenerate.button")}
        </button>
      </div>
      {job && job.status !== "idle" && (
        <div
          style={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {job.items.map((item, i) => (
            <div key={i} style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
              {item.scope} · {item.partner_name ?? item.day ?? ""} · {item.state}
              {item.state === "failed" && item.error ? ` — ${item.error}` : ""}
            </div>
          ))}
          {job.status === "done" && (
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              {t("users.detail.regenerate.summary", { done, failed })}
            </div>
          )}
        </div>
      )}
      {modal}
    </div>
  );
}
